/* src/components/TemplateSelector.jsx
 * Two-step template selection: Type → Layout with preview
 * On mobile, layout step is a full-screen popup that fits the viewport.
 */

import React, { useState, useEffect } from 'react';
import { getAllLayouts, getTemplatesByCategory, getLayout } from '../utils/formTemplates';
import { useIsMobile } from '../hooks/useIsMobile';

export default function TemplateSelector({ onSelect, onCancel, creating = false }) {
  const isMobile = useIsMobile();
  const [step, setStep] = useState('type'); // 'type' or 'layout'
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedLayout, setSelectedLayout] = useState(null);
  const [customTheme, setCustomTheme] = useState(null); // Custom theme colors

  // Auto-select the template's preferred layout (or the first layout) when chosen
  useEffect(() => {
    if (step === 'layout' && selectedTemplate && !selectedLayout) {
      const preferred = selectedTemplate.defaultLayout && getLayout(selectedTemplate.defaultLayout);
      const layouts = getAllLayouts();
      setSelectedLayout(preferred || layouts[0]);
    }
  }, [step, selectedTemplate, selectedLayout]);

  // Initialize custom theme when template is selected
  useEffect(() => {
    if (selectedTemplate && !customTheme) {
      setCustomTheme({ ...selectedTemplate.defaultTheme });
    }
  }, [selectedTemplate, customTheme]);

  const { general: generalTemplates, industry: industryTemplates } = getTemplatesByCategory();
  const layouts = getAllLayouts();

  function handleTemplateSelect(template) {
    if (creating) return;
    if (template === null) {
      // Blank form - skip layout selection
      onSelect({ template: null, layout: null });
      return;
    }
    setSelectedTemplate(template);
    setStep('layout');
  }

  function handleLayoutSelect(layout) {
    setSelectedLayout(layout);
    // Don't auto-submit, let user see preview and click button
  }

  function handleCreateForm() {
    if (creating) return;
    if (selectedTemplate && selectedLayout) {
      onSelect({ 
        template: selectedTemplate, 
        layout: selectedLayout,
        theme: customTheme || selectedTemplate.defaultTheme 
      });
    }
  }

  function updateThemeColor(colorKey, value) {
    if (customTheme) {
      setCustomTheme({
        ...customTheme,
        [colorKey]: value,
      });
    }
  }

  function handleBack() {
    if (step === 'layout') {
      setStep('type');
      setSelectedLayout(null);
    }
  }

  // Render preview of form with selected template and layout
  function renderPreview() {
    if (!selectedTemplate) return null;

    const previewFields = selectedTemplate.fields.slice(0, 3); // Show first 3 fields
    const layoutStyles = selectedLayout?.styles || {
      layout: 'single-column',
      spacing: 'comfortable',
      fieldStyle: 'rounded',
      buttonStyle: 'rounded',
    };
    
    // Use custom theme if available, otherwise default
    const theme = customTheme || selectedTemplate.defaultTheme;

    return (
      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        padding: '1rem',
        background: theme.backgroundColor,
        width: '100%',
        margin: '0 auto',
        transition: 'all 0.3s',
      }}>
        <div style={{
          fontSize: '1.2rem',
          fontWeight: 'bold',
          marginBottom: '1rem',
          color: theme.textColor,
        }}>
          {selectedTemplate.name}
        </div>
        
        {/* Show layout-specific preview indicators */}
        {layoutStyles.layout === 'multi-step' && (
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            background: '#e3f2fd', 
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: theme.textColor,
          }}>
            Multi-Step Form (3 steps with progress bar)
          </div>
        )}
        {layoutStyles.layout === 'accordion' && (
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            background: '#f3e5f5', 
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: theme.textColor,
          }}>
            Accordion Layout (Collapsible sections)
          </div>
        )}
        {layoutStyles.layout === 'tabbed' && (
          <div style={{ 
            marginBottom: '1rem', 
            padding: '0.75rem', 
            background: '#fff3e0', 
            borderRadius: '6px',
            fontSize: '0.85rem',
            color: theme.textColor,
          }}>
            Tabbed Layout (Tab navigation)
          </div>
        )}

        <div style={{
          display: layoutStyles.layout === 'two-column' ? 'grid' : 'block',
          gridTemplateColumns: layoutStyles.layout === 'two-column' ? '1fr 1fr' : '1fr',
          gap: layoutStyles.spacing === 'compact' ? '0.5rem' : layoutStyles.spacing === 'comfortable' ? '1rem' : '0.75rem',
        }}>
          {previewFields.map((field, idx) => (
            <div 
              key={idx} 
              style={{
                marginBottom: layoutStyles.spacing === 'compact' ? '0.5rem' : '0.75rem',
                padding: layoutStyles.fieldStyle === 'card' ? '0.75rem' : '0',
                border: layoutStyles.fieldStyle === 'card' ? `1px solid ${theme.borderColor}` : 'none',
                borderRadius: layoutStyles.fieldStyle === 'card' ? '8px' : '0',
                background: layoutStyles.fieldStyle === 'card' ? '#f8f9fa' : 'transparent',
              }}
            >
              <div style={{
                fontSize: '0.85rem',
                marginBottom: '0.25rem',
                color: theme.textColor,
                fontWeight: '500',
              }}>
                {field.label}
              </div>
              <div style={{
                width: '100%',
                padding: '0.5rem',
                border: layoutStyles.fieldStyle === 'minimal' ? 'none' : `1px solid ${theme.borderColor}`,
                borderBottom: layoutStyles.fieldStyle === 'minimal' ? `2px solid ${theme.borderColor}` : 'none',
                borderRadius: layoutStyles.fieldStyle === 'rounded' ? '6px' : 
                            layoutStyles.fieldStyle === 'minimal' ? '0' : '4px',
                background: theme.backgroundColor,
                height: '36px',
              }} />
            </div>
          ))}
        </div>

        <div style={{
          width: '100%',
          padding: '0.75rem',
          background: theme.buttonColor,
          color: theme.buttonTextColor,
          borderRadius: layoutStyles.buttonStyle === 'rounded-full' ? '999px' :
                       layoutStyles.buttonStyle === 'rounded' ? '6px' : '4px',
          textAlign: 'center',
          fontWeight: '500',
          marginTop: '1rem',
        }}>
          Submit
        </div>
      </div>
    );
  }

  function renderTemplateCard(template) {
    return (
      <div
        key={template.id}
        className={`template-card${creating ? ' is-disabled' : ''}`}
        onClick={() => !creating && handleTemplateSelect(template)}
        role="button"
        tabIndex={creating ? -1 : 0}
        aria-disabled={creating}
        onKeyDown={(e) => {
          if (creating) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleTemplateSelect(template);
          }
        }}
        style={creating ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
      >
        <div className="template-card-icon">{template.icon}</div>
        <h3 className="template-card-name">{template.name}</h3>
        <p className="template-card-desc">{template.description}</p>
      </div>
    );
  }

  // Step 1: Select Template Type (popup; on mobile fits screen)
  if (step === 'type') {
    return (
      <div className="fb-modal-overlay" style={{ padding: isMobile ? '1rem' : undefined }}>
        <div className={`fb-modal template-selector-modal${isMobile ? ' is-mobile' : ''}`}>
          <div className="fb-modal-header">
            <h2 className="fb-modal-title">Step 1: Choose Form Type</h2>
            <button type="button" className="fb-embed-close" onClick={onCancel} aria-label="Close">
              ×
            </button>
          </div>

          <div className="template-selector-grid">
            <div
              className={`template-card template-card-blank${creating ? ' is-disabled' : ''}`}
              onClick={() => !creating && handleTemplateSelect(null)}
              role="button"
              tabIndex={creating ? -1 : 0}
              aria-disabled={creating}
              onKeyDown={(e) => {
                if (creating) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleTemplateSelect(null);
                }
              }}
              style={creating ? { opacity: 0.5, pointerEvents: 'none' } : undefined}
            >
              <div className="template-card-icon">📝</div>
              <h3 className="template-card-name">Blank Form</h3>
              <p className="template-card-desc">Start from scratch</p>
            </div>
          </div>

          {industryTemplates.length > 0 && (
            <>
              <h3 className="template-section-label">By Industry</h3>
              <div className="template-selector-grid">
                {industryTemplates.map((template) => renderTemplateCard(template))}
              </div>
            </>
          )}

          {generalTemplates.length > 0 && (
            <>
              <h3 className="template-section-label">General</h3>
              <div className="template-selector-grid">
                {generalTemplates.map((template) => renderTemplateCard(template))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // Step 2: Select Layout with Preview (popup; on mobile fits screen, single column)
  return (
    <div
      className="fb-modal-overlay"
      style={{ padding: isMobile ? 0 : '1rem', alignItems: isMobile ? 'stretch' : 'center' }}
    >
      <div className={`fb-modal template-selector-modal template-selector-layout${isMobile ? ' is-mobile' : ''}`}>
        <div className="fb-modal-header">
          <div style={{ minWidth: 0 }}>
            <h2 className="fb-modal-title">Layout Options</h2>
            <p className="template-layout-subtitle">
              Template: <strong>{selectedTemplate.name}</strong>
            </p>
          </div>
          <button type="button" className="fb-embed-close" onClick={onCancel} aria-label="Close">
            ×
          </button>
        </div>

        <div className={`template-layout-grid${isMobile ? ' is-mobile' : ''}`}>
          {/* Layout Options */}
          <div>
            <div className="template-layout-toolbar">
              <h3 className="fb-subsection-title" style={{ margin: 0 }}>Layout Options</h3>
              <button type="button" className="inbox-pill-btn" onClick={handleBack}>
                Back
              </button>
            </div>

            <div className="template-layout-options">
              {layouts.map((layout) => (
                <div
                  key={layout.id}
                  className={`template-layout-option${selectedLayout?.id === layout.id ? ' is-selected' : ''}`}
                  onClick={() => handleLayoutSelect(layout)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleLayoutSelect(layout);
                    }
                  }}
                >
                  <div className="template-layout-option-inner">
                    <div style={{ flex: 1 }}>
                      <h4 className="template-layout-name">{layout.name}</h4>
                      <p className="template-layout-desc">{layout.description}</p>
                    </div>
                    {selectedLayout?.id === layout.id && (
                      <div className="template-layout-check">✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div>
            <h3 className="fb-subsection-title">Preview</h3>

            <div className="template-preview-frame">
              {renderPreview()}
            </div>

            {selectedLayout && (
              <button
                type="button"
                className="inbox-pill-btn-primary template-create-btn"
                onClick={handleCreateForm}
                disabled={creating}
              >
                {creating ? 'Creating...' : `Create Form with ${selectedLayout.name} Layout`}
              </button>
            )}
          </div>

          {/* Color Customization */}
          <div>
            <h3 className="fb-subsection-title">Customize Colors</h3>

            {customTheme && (
              <div className="template-color-fields">
                <div>
                  <label className="template-color-label">Primary Color</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.primaryColor}
                      onChange={(e) => updateThemeColor('primaryColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.primaryColor}
                      onChange={(e) => updateThemeColor('primaryColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="template-color-label">Background</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.backgroundColor}
                      onChange={(e) => updateThemeColor('backgroundColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.backgroundColor}
                      onChange={(e) => updateThemeColor('backgroundColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="template-color-label">Text Color</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.textColor}
                      onChange={(e) => updateThemeColor('textColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.textColor}
                      onChange={(e) => updateThemeColor('textColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="template-color-label">Border Color</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.borderColor}
                      onChange={(e) => updateThemeColor('borderColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.borderColor}
                      onChange={(e) => updateThemeColor('borderColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="template-color-label">Button Color</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.buttonColor}
                      onChange={(e) => updateThemeColor('buttonColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.buttonColor}
                      onChange={(e) => updateThemeColor('buttonColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <div>
                  <label className="template-color-label">Button Text</label>
                  <div className="template-color-row">
                    <input
                      type="color"
                      value={customTheme.buttonTextColor}
                      onChange={(e) => updateThemeColor('buttonTextColor', e.target.value)}
                      className="template-color-swatch"
                    />
                    <input
                      type="text"
                      value={customTheme.buttonTextColor}
                      onChange={(e) => updateThemeColor('buttonTextColor', e.target.value)}
                      className="fb-input template-color-text"
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="inbox-pill-btn template-reset-btn"
                  onClick={() => setCustomTheme({ ...selectedTemplate.defaultTheme })}
                >
                  Reset to Default
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
